import { useNavigate } from 'react-router';
import { useDashboard } from '../../lib/queries.js';
import { Stat, Card, CardHeader, CardBody } from '../../components/ui/index.js';
import { useCan } from '../../lib/auth-store.js';

const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

/**
 * Two bands per docs/03 §26.3. Every tile links to the list behind it — but
 * only for a principal that holds the permission that list demands, so a tile
 * never offers a destination the router would bounce (custom roles included).
 */
export function Dashboard() {
  const { data, isLoading, error } = useDashboard();
  const nav = useNavigate();
  const can = useCan();
  if (error) return <p className="text-sm text-red-600">Could not load the dashboard.</p>;
  const p = data?.product;
  const o = data?.ops;
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold">Dashboard</h1>
        <p className="text-xs text-grey-500">
          Product health and operational load. Refreshes every minute.
        </p>
      </div>
      <Card>
        <CardHeader
          title="Operational health"
          description="Queues, SLAs and money — what needs a human today."
        />
        <CardBody className="grid grid-cols-5 gap-3">
          <Stat
            label="Open reports"
            value={isLoading ? '…' : o!.openReports}
            tone={o && o.openReports > 50 ? 'amber' : 'neutral'}
            onClick={can('moderation:report:manage') ? () => nav('/moderation') : undefined}
          />
          <Stat
            label="SLA breached"
            value={isLoading ? '…' : o!.slaBreached}
            tone={o && o.slaBreached > 0 ? 'red' : 'green'}
            hint="24h target (NFR-12)"
            onClick={
              can('moderation:report:manage') ? () => nav('/moderation?sort=sla') : undefined
            }
          />
          <Stat
            label="Open tickets"
            value={isLoading ? '…' : o!.openTickets}
            onClick={can('support:ticket:manage') ? () => nav('/support') : undefined}
          />
          <Stat
            label="Pending upgrades"
            value={isLoading ? '…' : o!.pendingUpgrades}
            tone={o && o.pendingUpgrades > 0 ? 'amber' : 'neutral'}
            onClick={can('standing:upgrade:manage') ? () => nav('/standing') : undefined}
          />
          <Stat
            label="Payment success"
            value={isLoading ? '…' : pct(o!.paymentSuccessRate)}
            tone={o && o.paymentSuccessRate < 0.97 ? 'red' : 'green'}
            hint="target ≥97%"
            onClick={can('payment:read') ? () => nav('/commerce') : undefined}
          />
        </CardBody>
      </Card>
      <Card>
        <CardHeader
          title="Product health"
          description="The seven success measures from the PRD §3.2 — the ones instrumented so far."
        />
        <CardBody className="grid grid-cols-5 gap-3">
          <Stat label="Members" value={isLoading ? '…' : p!.members.toLocaleString()} />
          <Stat
            label="New profiles (7d)"
            value={isLoading ? '…' : p!.activated7d}
            hint="activation proxy"
          />
          <Stat
            label="Posts this week"
            value={isLoading ? '…' : p!.postsThisWeek}
            hint="position rate proxy"
          />
          <Stat
            label="Paid members"
            value={isLoading ? '…' : p!.paidMembers}
            hint={p ? `${pct(p.members ? p.paidMembers / p.members : 0)} conversion` : undefined}
          />
          <Stat
            label="Donation success"
            value={isLoading ? '…' : pct(p!.donationSuccessRate)}
            tone={p && p.donationSuccessRate < 0.97 ? 'red' : 'green'}
          />
        </CardBody>
      </Card>
    </div>
  );
}
